import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { endOfDay, parseISO } from 'date-fns'
import { Modal } from '../modal'
import { Input } from '../input'
import { Button } from '../button'
import { usePermissions } from 'src/hooks/use-permissions'
import { userTokenApi } from 'src/services/user-tokens/api'
import { userTokenQueries, UserTokenCreated } from 'src/services/user-tokens'
import { BOT_PERMISSION_GROUPS } from '../bot-tokens/bot-permission-groups'
import type { BotPermissionGroup } from '../bot-tokens/bot-permission-groups'

/**
 * The module permission catalog (shared with bot tokens) narrowed to only the
 * permissions the current user actually holds. A user can never grant a token a
 * permission they don't have, so anything they lack is simply not offered.
 */
function usePickablePermissionGroups(): BotPermissionGroup[] {
  const { permissions } = usePermissions()
  return useMemo(() => {
    const held = new Set(permissions)
    return BOT_PERMISSION_GROUPS.map((group) => {
      const access = group.access && held.has(group.access.codename) ? group.access : null
      const actions = group.permissions.filter((p) => held.has(p.codename))
      // Actions require the module access permission to be meaningful, so drop
      // them if the user lacks the module's access grant.
      return { ...group, access, permissions: access ? actions : [] }
    }).filter((group) => group.access || group.permissions.length)
  }, [permissions])
}

function PermissionGroupRow({
  group,
  selectedPermissions,
  onToggle,
}: {
  group: BotPermissionGroup
  selectedPermissions: Set<string>
  onToggle: (codename: string) => void
}) {
  return (
    <div
      className="rounded-md border border-border bg-surface-hover p-3"
      data-testid={`user-permission-group-${group.module}`}
    >
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-secondary">
        {group.label}
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-1">
        {group.access && (
          <label className="flex items-center gap-1.5 text-sm text-text-primary">
            <input
              type="checkbox"
              checked={selectedPermissions.has(group.access.codename)}
              onChange={() => onToggle(group.access!.codename)}
              className="h-4 w-4 rounded border-border-strong text-primary-500 focus:ring-primary-500"
              data-testid={`user-perm-${group.access.codename}`}
            />
            {group.access.label}
          </label>
        )}
        {group.permissions.map((perm) => (
          <label
            key={perm.codename}
            className="flex items-center gap-1.5 text-sm text-text-primary"
          >
            <input
              type="checkbox"
              checked={selectedPermissions.has(perm.codename)}
              onChange={() => onToggle(perm.codename)}
              className="h-4 w-4 rounded border-border-strong text-primary-500 focus:ring-primary-500"
              data-testid={`user-perm-${perm.codename}`}
            />
            {perm.label}
          </label>
        ))}
      </div>
    </div>
  )
}

interface CreateUserTokenModalProps {
  isOpen: boolean
  onClose: () => void
  /** Called with the created token payload so the parent can reveal the secret. */
  onCreated: (created: UserTokenCreated) => void
}

export const CreateUserTokenModal: React.FC<CreateUserTokenModalProps> = ({
  isOpen,
  onClose,
  onCreated,
}) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const groups = usePickablePermissionGroups()
  const [name, setName] = useState('')
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(new Set())
  const [expiresAt, setExpiresAt] = useState('')
  const [apiError, setApiError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen) {
      setName('')
      setSelectedPermissions(new Set())
      setExpiresAt('')
      setApiError(null)
    }
  }, [isOpen])

  const togglePermission = useCallback(
    (codename: string) => {
      setSelectedPermissions((prev) => {
        const next = new Set(prev)
        if (next.has(codename)) {
          next.delete(codename)
          // Deselecting a module's access clears its action permissions too.
          const group = groups.find((g) => g.access?.codename === codename)
          if (group) {
            for (const perm of group.permissions) next.delete(perm.codename)
          }
        } else {
          next.add(codename)
          // Selecting an action auto-selects the module access permission.
          const group = groups.find((g) => g.permissions.some((p) => p.codename === codename))
          if (group?.access) next.add(group.access.codename)
        }
        return next
      })
    },
    [groups],
  )

  const { mutate: createToken, isPending } = useMutation({
    mutationFn: () =>
      userTokenApi.csc.createUserToken({
        name: name.trim(),
        permissions: Array.from(selectedPermissions),
        // The token stays valid through the picked day, in the user's zone.
        // `new Date('YYYY-MM-DD')` is UTC midnight, which displays as the day
        // before anywhere west of UTC.
        expiresAt: expiresAt ? endOfDay(parseISO(expiresAt)).toISOString() : null,
      }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: userTokenQueries.all() })
      onCreated(created)
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.detail ||
        error?.response?.data?.name?.[0] ||
        error?.response?.data?.permissions?.[0] ||
        error?.response?.data?.expires_at?.[0] ||
        error?.response?.data?.non_field_errors?.[0] ||
        t('userTokens.createError')
      setApiError(message)
    },
  })

  const isValid = name.trim().length > 0 && selectedPermissions.size > 0

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setApiError(null)
    if (!isValid) return
    createToken()
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('userTokens.createToken')} size="lg">
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <Input
            label={t('common.name')}
            placeholder={t('userTokens.namePlaceholder')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            data-testid="user-token-name-input"
          />
        </div>

        <div>
          <h4 className="mb-3 text-sm font-medium text-text-primary">
            {t('userTokens.permissions')}
          </h4>
          {groups.length === 0 ? (
            <p className="text-sm text-text-placeholder" data-testid="user-token-no-permissions">
              {t('userTokens.noPermissionsToGrant')}
            </p>
          ) : (
            <div className="space-y-3">
              {groups.map((group) => (
                <PermissionGroupRow
                  key={group.module}
                  group={group}
                  selectedPermissions={selectedPermissions}
                  onToggle={togglePermission}
                />
              ))}
            </div>
          )}
        </div>

        <div>
          <Input
            label={t('userTokens.expirationDate')}
            type="date"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            data-testid="user-token-expires-input"
          />
        </div>

        {apiError && (
          <p className="text-sm text-error" data-testid="user-token-form-error">
            {apiError}
          </p>
        )}

        <div className="flex justify-end space-x-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            isLoading={isPending}
            disabled={!isValid}
            data-testid="user-token-form-submit"
          >
            {t('userTokens.createToken')}
          </Button>
        </div>
      </form>
    </Modal>
  )
}
